import { AnalysisCollection, SavedAnalysis, MortgageNoteInputs } from '../types';

/**
 * Check if File System Access API is supported
 */
function isFileSystemAccessSupported(): boolean {
  return 'showSaveFilePicker' in window && 'showOpenFilePicker' in window;
}

/**
 * Generate a unique analysis ID
 */
export function generateAnalysisId(): string {
  return `analysis_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
}

/**
 * Default input values for a new analysis
 */
export const defaultInputs: MortgageNoteInputs = {
  noteName: '',
  listedPurchasePrice: 0,
  tradeDeskFee: 0,
  monthlyServicingFee: 0,
  titlingFees: 0,
  otherOneTimeFee: 0,
  otherMonthlyFee: 0,
  rate: 0,
  principalAndInterest: 0,
  paymentsRemaining: 0,
  desiredPurchasePrice: 0,
};

/**
 * Create a new analysis with default values
 */
export function createDefaultAnalysis(id?: string): SavedAnalysis {
  const now = new Date().toISOString();
  return {
    id: id || generateAnalysisId(),
    inputs: { ...defaultInputs },
    createdAt: now,
    updatedAt: now,
  };
}

/**
 * Save analyses to a JSON file
 */
export async function saveAnalysesToFile(analyses: SavedAnalysis[]): Promise<void> {
  const collection: AnalysisCollection = {
    version: '1.0.0',
    exportedAt: new Date().toISOString(),
    analyses,
  };

  const json = JSON.stringify(collection, null, 2);
  const blob = new Blob([json], { type: 'application/json' });

  if (isFileSystemAccessSupported()) {
    // Modern File System Access API
    const handle = await window.showSaveFilePicker({
      suggestedName: 'mortgage-analyses.json',
      types: [
        {
          description: 'JSON Files',
          accept: { 'application/json': ['.json'] },
        },
      ],
    });
    const writable = await handle.createWritable();
    await writable.write(blob);
    await writable.close();
  } else {
    // Fallback: download link for browsers without File System Access API
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'mortgage-analyses.json';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  }
}

/**
 * Load analyses from a JSON file
 */
export async function loadAnalysesFromFile(): Promise<SavedAnalysis[]> {
  if (isFileSystemAccessSupported()) {
    // Modern File System Access API
    const [handle] = await window.showOpenFilePicker({
      types: [
        {
          description: 'JSON Files',
          accept: { 'application/json': ['.json'] },
        },
      ],
      multiple: false,
    });
    const file = await handle.getFile();
    const text = await file.text();
    const collection = JSON.parse(text) as AnalysisCollection;
    return validateAndMigrateCollection(collection);
  } else {
    // Fallback: file input for browsers without File System Access API
    return new Promise((resolve, reject) => {
      const input = document.createElement('input');
      input.type = 'file';
      input.accept = '.json,application/json';
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) {
          reject(new Error('No file selected'));
          return;
        }
        try {
          const text = await file.text();
          const collection = JSON.parse(text) as AnalysisCollection;
          resolve(validateAndMigrateCollection(collection));
        } catch (err) {
          reject(err);
        }
      };
      input.oncancel = () => {
        reject(new DOMException('User cancelled', 'AbortError'));
      };
      input.click();
    });
  }
}

/**
 * Validate and migrate loaded data to current schema
 */
function validateAndMigrateCollection(collection: AnalysisCollection): SavedAnalysis[] {
  // Basic validation
  if (!collection.version || !Array.isArray(collection.analyses)) {
    throw new Error('Invalid file format');
  }

  // Validate and ensure each analysis has required fields
  return collection.analyses.map((analysis) => {
    if (!analysis.inputs) {
      throw new Error('Invalid analysis: missing inputs');
    }

    return {
      id: analysis.id || generateAnalysisId(),
      inputs: {
        ...defaultInputs,
        ...analysis.inputs,
      },
      createdAt: analysis.createdAt || new Date().toISOString(),
      updatedAt: analysis.updatedAt || new Date().toISOString(),
    };
  });
}
